import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface DonateModalProps {
  onClose: () => void;
}

const PRESET_AMOUNTS = [10, 25, 50, 100, 250];
const PAYPAL_EMAIL = 'info@gtraweb3.com';

function buildPayPalUrl(amount: number, currency: string): string {
  const base = 'https://www.paypal.com/donate/?business=' + encodeURIComponent(PAYPAL_EMAIL);
  return base + '&currency_code=' + currency + (amount > 0 ? '&amount=' + amount.toFixed(2) : '');
}

function generateCertId(): string {
  const year = new Date().getFullYear();
  const rand = Math.floor(100000 + Math.random() * 900000);
  return `GTRA-${year}-${rand}`;
}

/* ───────────── Certificate View ───────────── */
function CertificateView({
  isZh,
  amount,
  currency,
  donorName,
  certId,
  onClose,
}: {
  isZh: boolean;
  amount: number;
  currency: string;
  donorName: string;
  certId: string;
  onClose: () => void;
}) {
  const [show, setShow] = useState(false);
  const symbol = currency === 'USD' ? '$' : currency === 'EUR' ? '€' : '¥';
  const dateStr = new Date().toLocaleDateString(isZh ? 'zh-CN' : 'en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
  });

  useEffect(() => {
    const t = setTimeout(() => setShow(true), 80);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className={`transition-all duration-500 ${show ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
      {/* Success animation top */}
      <div className="relative bg-gradient-to-br from-[#0A2540] via-[#0d3060] to-[#1B4F72] px-6 pt-8 pb-12 overflow-hidden">
        <div className="absolute -top-6 -right-6 w-36 h-36 rounded-full bg-white/5" />
        <div className="absolute -bottom-8 -left-8 w-44 h-44 rounded-full bg-[#E8A838]/10" />
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white/70 hover:text-white transition-all cursor-pointer"
        >
          <i className="ri-close-line" />
        </button>
        <div className="relative z-10 flex flex-col items-center text-center">
          {/* Animated checkmark */}
          <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#E8A838]/20 mb-4 ring-4 ring-[#E8A838]/30">
            <i className="ri-checkbox-circle-fill text-[#E8A838] text-4xl" />
          </div>
          <h2 className="text-white font-bold text-xl mb-1">
            {isZh ? '感谢您的支持！' : 'Thank You for Your Support!'}
          </h2>
          <p className="text-white/60 text-sm">
            {isZh ? 'PayPal 页面已在新标签页打开，请完成支付' : 'PayPal has opened in a new tab. Please complete your payment there.'}
          </p>
        </div>
      </div>

      {/* Certificate Card */}
      <div className="p-5">
        <div
          className="rounded-2xl overflow-hidden border-2 border-[#E8A838]/30"
          style={{ background: 'linear-gradient(135deg, #FFFDF5 0%, #FFF8E7 50%, #FFFDF5 100%)' }}
        >
          {/* Certificate top bar */}
          <div className="h-1.5" style={{ background: 'linear-gradient(90deg, #C9A84C, #E8A838, #F5C842, #E8A838, #C9A84C)' }} />

          <div className="px-6 py-5">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-[#8B6914] text-[10px] font-bold tracking-widest uppercase">
                  {isZh ? '捐赠证书' : 'Certificate of Donation'}
                </p>
                <p className="text-[#0A2540]/40 text-[9px] mt-0.5">{certId}</p>
              </div>
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#E8A838]/15 border border-[#E8A838]/30">
                <i className="ri-medal-line text-[#C9A84C] text-lg" />
              </div>
            </div>

            {/* Divider */}
            <div className="h-px bg-[#E8A838]/20 mb-4" />

            {/* Main content */}
            <div className="text-center mb-4">
              <p className="text-[#0A2540]/50 text-xs mb-1">
                {isZh ? '全球科创圆桌联盟 荣幸地证明' : 'Global Tech Roundtable Alliance gratefully acknowledges'}
              </p>
              <p className="text-[#0A2540] font-bold text-lg leading-snug mb-1">
                {donorName || (isZh ? '尊贵捐赠人' : 'Distinguished Donor')}
              </p>
              <p className="text-[#0A2540]/50 text-xs mb-3">
                {isZh ? '已慷慨捐赠' : 'has generously donated'}
              </p>
              <div className="inline-block px-6 py-2 rounded-xl bg-[#E8A838]/15 border border-[#E8A838]/25">
                <span className="text-[#8B6914] font-black text-2xl">{symbol}{amount}</span>
                <span className="text-[#8B6914]/60 text-sm ml-1">{currency}</span>
              </div>
              <p className="text-[#0A2540]/40 text-xs mt-3">
                {isZh ? '用于支持全球科技创新与知识普惠事业' : 'in support of global science innovation and knowledge advancement'}
              </p>
            </div>

            <div className="h-px bg-[#E8A838]/20 mb-4" />

            {/* Footer */}
            <div className="flex items-end justify-between">
              <div>
                <p className="text-[#0A2540]/40 text-[10px] mb-0.5">{isZh ? '捐赠日期' : 'Date'}</p>
                <p className="text-[#0A2540] text-xs font-semibold">{dateStr}</p>
              </div>
              <div className="text-right">
                <p className="text-[#0A2540]/40 text-[10px] mb-0.5">{isZh ? '联盟主席' : 'Alliance President'}</p>
                <p className="text-[#0A2540] text-xs font-semibold italic">黄晓斌 / Huang Xiaobin</p>
                <div className="w-16 h-0.5 bg-[#E8A838]/40 ml-auto mt-1" />
              </div>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="h-1" style={{ background: 'linear-gradient(90deg, #C9A84C, #E8A838, #F5C842, #E8A838, #C9A84C)' }} />
        </div>

        {/* Actions */}
        <div className="flex gap-3 mt-4">
          <button
            onClick={() => window.print()}
            className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border border-[#0A2540]/15 text-[#0A2540]/70 text-sm font-medium hover:border-[#0A2540]/30 hover:text-[#0A2540] transition-all cursor-pointer whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-printer-line" />
            </div>
            {isZh ? '打印证书' : 'Print'}
          </button>
          <button
            onClick={onClose}
            className="flex-1 py-3 rounded-xl bg-[#0A2540] hover:bg-[#0A2540]/90 text-white text-sm font-semibold transition-all cursor-pointer whitespace-nowrap"
          >
            {isZh ? '完成' : 'Done'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ───────────── Main Modal ───────────── */
export default function DonateModal({ onClose }: DonateModalProps) {
  const { i18n } = useTranslation();
  const isZh = i18n.language === 'zh';

  const [selectedAmount, setSelectedAmount] = useState<number | 'custom'>(25);
  const [customAmount, setCustomAmount] = useState('');
  const [currency, setCurrency] = useState<'USD' | 'EUR' | 'CNY'>('USD');
  const [payMethod, setPayMethod] = useState<'paypal' | 'stripe'>('paypal');
  const [donorName, setDonorName] = useState('');
  const [donated, setDonated] = useState(false);
  const [certId] = useState(generateCertId);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKey);
    };
  }, [onClose]);

  const finalAmount = selectedAmount === 'custom'
    ? (parseFloat(customAmount) || 0)
    : selectedAmount;

  const currencySymbol = currency === 'USD' ? '$' : currency === 'EUR' ? '€' : '¥';

  const handleDonate = () => {
    if (payMethod === 'paypal' && finalAmount > 0) {
      const url = buildPayPalUrl(finalAmount, currency);
      window.open(url, '_blank', 'noopener,noreferrer');
      setDonated(true);
    }
  };

  const impacts = isZh
    ? [
        { amount: 10, desc: '支持一位研究者参与线上圆桌讨论' },
        { amount: 25, desc: '资助一份科技政策简报的制作与发布' },
        { amount: 50, desc: '支持一场区域科创交流小组活动' },
        { amount: 100, desc: '助力一名青年科学家参加峰会' },
        { amount: 250, desc: '赞助一个跨国联合研究项目启动' },
      ]
    : [
        { amount: 10, desc: 'Supports a researcher in an online roundtable' },
        { amount: 25, desc: 'Funds production of a science policy brief' },
        { amount: 50, desc: 'Supports a regional innovation exchange group' },
        { amount: 100, desc: 'Helps a young scientist attend a summit' },
        { amount: 250, desc: 'Sponsors the launch of a cross-border research project' },
      ];

  const currentImpact = impacts.find((i) => i.amount === selectedAmount);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      <div className="relative bg-white rounded-2xl w-full max-w-md overflow-hidden flex flex-col shadow-2xl">
        {donated ? (
          <CertificateView
            isZh={isZh}
            amount={finalAmount}
            currency={currency}
            donorName={donorName}
            certId={certId}
            onClose={onClose}
          />
        ) : (
          <>
            {/* Header */}
            <div className="relative bg-gradient-to-br from-[#0A2540] via-[#0d3060] to-[#1B4F72] px-6 pt-8 pb-10 overflow-hidden">
              <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-white/5" />
              <div className="absolute -bottom-10 -left-10 w-48 h-48 rounded-full bg-[#2C7DA0]/15" />
              <button
                onClick={onClose}
                className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white/70 hover:text-white transition-all cursor-pointer"
              >
                <i className="ri-close-line" />
              </button>
              <div className="relative z-10">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#E8A838]/20 mb-4">
                  <i className="ri-heart-fill text-[#E8A838] text-2xl" />
                </div>
                <h2 className="text-white font-bold text-2xl mb-1.5">
                  {isZh ? '支持 GTRA' : 'Support GTRA'}
                </h2>
                <p className="text-white/60 text-sm leading-relaxed">
                  {isZh
                    ? '每一份捐赠都将推动全球科创合作与知识传播'
                    : 'Every donation advances global science and innovation collaboration'}
                </p>
              </div>
            </div>

            {/* Body */}
            <div className="p-6 space-y-5 overflow-y-auto max-h-[65vh]">
              {/* Currency selector */}
              <div className="flex items-center gap-1 bg-[#F7F9FC] rounded-xl p-1">
                {(['USD', 'EUR', 'CNY'] as const).map((cur) => (
                  <button
                    key={cur}
                    onClick={() => setCurrency(cur)}
                    className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                      currency === cur ? 'bg-white text-[#0A2540] shadow-sm' : 'text-[#0A2540]/50 hover:text-[#0A2540]'
                    }`}
                  >
                    {cur}
                  </button>
                ))}
              </div>

              {/* Amount presets */}
              <div>
                <p className="text-[#0A2540]/50 text-xs font-medium mb-2.5">{isZh ? '选择金额' : 'Select Amount'}</p>
                <div className="grid grid-cols-3 gap-2 mb-2">
                  {PRESET_AMOUNTS.map((amt) => (
                    <button
                      key={amt}
                      onClick={() => { setSelectedAmount(amt); setCustomAmount(''); }}
                      className={`py-2.5 rounded-xl text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
                        selectedAmount === amt ? 'bg-[#0A2540] text-white' : 'bg-[#F7F9FC] text-[#0A2540] hover:bg-[#0A2540]/10'
                      }`}
                    >
                      {currencySymbol}{amt}
                    </button>
                  ))}
                  <button
                    onClick={() => setSelectedAmount('custom')}
                    className={`py-2.5 rounded-xl text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                      selectedAmount === 'custom' ? 'bg-[#0A2540] text-white' : 'bg-[#F7F9FC] text-[#0A2540]/60 hover:bg-[#0A2540]/10'
                    }`}
                  >
                    {isZh ? '自定义' : 'Custom'}
                  </button>
                </div>
                {selectedAmount === 'custom' && (
                  <div className="relative">
                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#0A2540]/40 text-sm font-semibold">{currencySymbol}</span>
                    <input
                      type="number"
                      min="1"
                      placeholder="0"
                      value={customAmount}
                      onChange={(e) => setCustomAmount(e.target.value)}
                      className="w-full pl-8 pr-4 py-3 border border-[#0A2540]/15 rounded-xl text-sm text-[#0A2540] focus:outline-none focus:border-[#2C7DA0] transition-colors"
                      autoFocus
                    />
                  </div>
                )}
              </div>

              {/* Impact hint */}
              {currentImpact && (
                <div className="flex items-start gap-2.5 px-3.5 py-3 bg-[#2C7DA0]/8 rounded-xl">
                  <div className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0 text-[#2C7DA0]">
                    <i className="ri-sparkling-2-line" />
                  </div>
                  <p className="text-[#2C7DA0] text-xs leading-relaxed">{currentImpact.desc}</p>
                </div>
              )}

              {/* Donor name (optional, for certificate) */}
              <div>
                <p className="text-[#0A2540]/50 text-xs font-medium mb-2">
                  {isZh ? '您的姓名（将显示在证书上，选填）' : 'Your Name (optional, for certificate)'}
                </p>
                <input
                  type="text"
                  value={donorName}
                  onChange={(e) => setDonorName(e.target.value)}
                  placeholder={isZh ? '请输入姓名或机构名称' : 'Enter your name or organization'}
                  className="w-full px-4 py-2.5 border border-[#0A2540]/15 rounded-xl text-sm text-[#0A2540] focus:outline-none focus:border-[#2C7DA0] transition-colors placeholder-[#0A2540]/30"
                />
              </div>

              {/* Payment Method */}
              <div>
                <p className="text-[#0A2540]/50 text-xs font-medium mb-2.5">{isZh ? '支付方式' : 'Payment Method'}</p>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setPayMethod('paypal')}
                    className={`flex flex-col items-center gap-2 py-3.5 px-3 rounded-xl border-2 transition-all cursor-pointer ${
                      payMethod === 'paypal' ? 'border-[#0070BA] bg-[#0070BA]/5' : 'border-[#0A2540]/10 hover:border-[#0A2540]/25'
                    }`}
                  >
                    <div className="w-8 h-8 flex items-center justify-center">
                      <span className="font-black text-xl" style={{ color: '#003087', fontFamily: 'serif', fontStyle: 'italic' }}>P</span>
                    </div>
                    <span className="text-xs font-bold text-[#003087]">PayPal</span>
                  </button>
                  <button
                    onClick={() => setPayMethod('stripe')}
                    className={`flex flex-col items-center gap-2 py-3.5 px-3 rounded-xl border-2 transition-all cursor-pointer relative ${
                      payMethod === 'stripe' ? 'border-[#635BFF] bg-[#635BFF]/5' : 'border-[#0A2540]/10 hover:border-[#0A2540]/25'
                    }`}
                  >
                    <div className="w-8 h-8 flex items-center justify-center">
                      <span className="font-black text-xl" style={{ color: '#635BFF', fontFamily: 'sans-serif' }}>S</span>
                    </div>
                    <span className="text-xs font-bold" style={{ color: '#635BFF' }}>Stripe</span>
                    <span className="absolute top-1.5 right-1.5 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-600 text-[9px] font-bold whitespace-nowrap">
                      {isZh ? '即将开放' : 'Coming Soon'}
                    </span>
                  </button>
                </div>
              </div>

              {payMethod === 'stripe' && (
                <div className="flex items-start gap-2.5 px-3.5 py-3 bg-amber-50 rounded-xl border border-amber-200">
                  <div className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0 text-amber-500">
                    <i className="ri-information-line" />
                  </div>
                  <p className="text-amber-700 text-xs leading-relaxed">
                    {isZh
                      ? 'Stripe 支付正在接入中，敬请期待。现在请选择 PayPal 完成捐赠，感谢您的支持！'
                      : 'Stripe payment integration is coming soon. Please use PayPal for your donation now. Thank you!'}
                  </p>
                </div>
              )}

              <button
                onClick={handleDonate}
                disabled={payMethod === 'stripe' || finalAmount <= 0}
                className={`w-full py-4 rounded-xl text-sm font-bold transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 ${
                  payMethod === 'stripe' || finalAmount <= 0
                    ? 'bg-[#0A2540]/20 text-[#0A2540]/30 cursor-not-allowed'
                    : 'bg-[#0A2540] hover:bg-[#0A2540]/90 text-white'
                }`}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={payMethod === 'paypal' ? 'ri-paypal-line' : 'ri-bank-card-line'} />
                </div>
                {payMethod === 'paypal'
                  ? (isZh
                      ? `通过 PayPal 捐赠 ${currencySymbol}${finalAmount > 0 ? finalAmount : '...'}`
                      : `Donate ${currencySymbol}${finalAmount > 0 ? finalAmount : '...'} via PayPal`)
                  : (isZh ? '暂不可用' : 'Not Available Yet')
                }
              </button>

              <div className="flex items-center justify-center gap-4 text-[#0A2540]/30 text-[10px]">
                <div className="flex items-center gap-1"><i className="ri-shield-check-line" /><span>{isZh ? '安全加密' : 'Secure & Encrypted'}</span></div>
                <div className="flex items-center gap-1"><i className="ri-global-line" /><span>{isZh ? '全球适用' : 'Globally Accepted'}</span></div>
                <div className="flex items-center gap-1"><i className="ri-heart-line" /><span>{isZh ? '感谢支持' : 'Thank You'}</span></div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
